import type { ProjectVersion_Insert_Input } from "@/graphql";

import type { ReferencedAreaEntryType } from "../types/ReferencedAreasType";

// The pure half of the reference-area rail: the rows a save sends and the trail row that describes it.
// Kept apart from the write itself so it can be tested without loading the Apollo client.

export interface ProjectReferenceAreasWriteInput {
  projectId: string;
  entries: ReferencedAreaEntryType[];
}

export interface ProjectReferenceAreaRowInput {
  projectId: string;
  referenceAreaTypeKey: string;
  area: number | null;
  description: string | null;
}

/** One row per entry that picked a type, the API's own shape: a blank description is stored as null. */
export const referenceAreaRows = (input: ProjectReferenceAreasWriteInput): ProjectReferenceAreaRowInput[] =>
  input.entries
    .filter((entry) => entry.type !== "")
    .map((entry) => ({
      projectId: input.projectId,
      referenceAreaTypeKey: entry.type,
      area: entry.area,
      description: entry.description?.trim() ? entry.description : null,
    }));

const areaByType = (entries: ReferencedAreaEntryType[]): Map<string, number | null> =>
  new Map(entries.filter((entry) => entry.type !== "").map((entry) => [entry.type, entry.area]));

const areaText = (area: number | null | undefined): string | null => (area === null || area === undefined ? null : String(area));

/**
 * The trail row. "project", not a new entity type: project_version_entity_type_check is a Postgres CHECK
 * constraint, and the areas are project base information, filed under the Base info chip like the
 * calculation and the address. One change per type whose area moved, was added or was removed; with no
 * baseline every type is listed with an unknown `from`.
 */
export const buildReferenceAreasVersionInput = (
  input: ProjectReferenceAreasWriteInput,
  previousEntries: ReferencedAreaEntryType[] | null,
  labelFor: (typeKey: string) => string = (typeKey) => typeKey
): ProjectVersion_Insert_Input => {
  const next = areaByType(input.entries);
  const previous = previousEntries === null ? null : areaByType(previousEntries);
  const typeKeys = new Set([...next.keys(), ...(previous?.keys() ?? [])]);

  const changes = [...typeKeys]
    .filter(
      (typeKey) => previous === null || previous.get(typeKey) !== next.get(typeKey) || previous.has(typeKey) !== next.has(typeKey)
    )
    .map((typeKey) => ({
      field: labelFor(typeKey),
      from: previous?.has(typeKey) ? areaText(previous.get(typeKey)) : null,
      to: next.has(typeKey) ? areaText(next.get(typeKey)) : null,
      unit: "m²",
    }));

  const single = changes.length === 1 ? changes[0] : null;

  return {
    projectId: input.projectId,
    entityType: "project",
    operation: "update",
    summary: single ? `Reference area "${single.field}" updated` : "Reference areas updated",
    entityLabel: single?.field ?? null,
    changes,
  };
};
